import { SectionTitle } from "@/components/SectionHeader";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Eyebrow } from "@/components/Eyebrow";
import {
  AnchorIcon,
  LightIcon,
  SliderIcon,
  WindIcon,
} from "@/components/icons";
import climate from "@/assets/how-it-works/climate.webp";
import controls from "@/assets/how-it-works/controls.webp";
import hotelSystems from "@/assets/how-it-works/hotel-systems.webp";
import lighting from "@/assets/how-it-works/lighting.webp";
import { cn } from "@/lib/cn";
import { motion, useInView, useReducedMotion } from "motion/react";
import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";

const SYSTEMS = [
  {
    title: "Lighting",
    image: lighting,
    imageAlt: "Guest room lit by Somo lighting scenes",
    body: "Create lighting experiences while giving properties centralized control and visibility.",
    Icon: LightIcon,
  },
  {
    title: "Climate",
    image: climate,
    imageAlt: "Guest room with Somo climate control",
    body: "Connect HVAC to occupancy and room conditions, automatically adjusting climate for guest comfort while reducing energy use when rooms are unoccupied.",
    Icon: WindIcon,
  },
  {
    title: "Controls",
    image: controls,
    imageAlt: "Somo guest controls on a room wall",
    body: "Give guests beautifully simple control over lighting, privacy, and temperature through switches, thermostats, and interfaces designed to complement the room.",
    Icon: SliderIcon,
  },
  {
    title: "Hotel Systems",
    image: hotelSystems,
    imageAlt: "Hotel room connected to the property's systems",
    body: "Connect Somo with the technology already running the property, creating a more coordinated ecosystem.",
    Icon: AnchorIcon,
  },
];

export function HowItWorksSection() {
  const [active, setActive] = useState(0);
  // A slide runs to completion before the next one starts, then heads straight
  // for the latest choice, so only its two photos are ever in the frame.
  // Auto-advance slides forward even when it wraps to the first item, so the
  // loop reads as one direction.
  const [slide, setSlide] = useState({ from: 0, to: 0, forward: false });
  const latest = useRef(0);
  const select = (index: number, forward = false) => {
    setActive(index);
    latest.current = index;
    setSlide((current) =>
      current.from === current.to && index !== current.to
        ? { from: current.to, to: index, forward }
        : current,
    );
  };
  const finishSlide = () =>
    setSlide((current) => ({
      from: current.to,
      to: latest.current,
      forward: false,
    }));
  const [autoPlay, setAutoPlay] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduceMotion = useReducedMotion();
  // Tablets show every item open in a 2×2 grid so rows never jump.
  const isTabletGrid = useMediaQuery(
    "(min-width: 768px) and (max-width: 1023px)",
  );
  const ref = useRef<HTMLElement>(null);
  const isNear = useInView(ref, { once: true, margin: "400px" });
  const isVisible = useInView(ref, { amount: 0.2 });
  useEffect(() => {
    if (isNear) {
      for (const system of SYSTEMS) {
        new Image().src = system.image;
      }
    }
  }, [isNear]);

  return (
    <section
      ref={ref}
      id="how-it-works"
      className="relative overflow-hidden px-8 pb-16 pt-24 md:px-6 lg:pt-32"
    >
      {/* The heading rests at the top of the photo and the list at its bottom.
          Every open body reserves the longest body's height, so switching
          items never changes the list's height or moves the section. */}
      <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[45fr_55fr] lg:grid-rows-[auto_1fr] lg:gap-x-16">
        <div className="flex flex-col gap-6 lg:col-start-1 lg:row-start-1">
          <Eyebrow>One connected environment</Eyebrow>
          <SectionTitle>Spaces That Respond as One</SectionTitle>
        </div>
        {/* Extends from its column to 16px in from the viewport, or to the
            1536px frame's edge on wider screens. */}
        <div
          className="relative aspect-[808/768] overflow-hidden -mx-4 rounded-card md:mx-0 md:aspect-auto md:h-120 lg:col-start-2 lg:aspect-[808/768] lg:h-auto lg:row-span-2 lg:row-start-1 lg:w-[calc(100%+max(0px,min((100vw-2rem-72rem)/2,12rem)))]"
          onPointerEnter={(event) =>
            event.pointerType === "mouse" && setHovered(true)
          }
          onPointerLeave={() => setHovered(false)}
        >
          {/* The chosen photo slides in from the side of the list it's on:
              from below for a later item, from above for an earlier one, 20px
              from the photo it replaces and never through the ones between. */}
          {slide.from === slide.to ? (
            <PhotoSlot key={`still-${slide.to}`} system={SYSTEMS[slide.to]} />
          ) : (
            <Slide
              key={`${slide.from}-${slide.to}`}
              from={SYSTEMS[slide.from]}
              to={SYSTEMS[slide.to]}
              direction={slide.forward || slide.to > slide.from ? 1 : -1}
              duration={reduceMotion ? 0 : SLIDE_SECONDS}
              onDone={finishSlide}
            />
          )}
        </div>
        <ul
          className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-x-10 md:gap-y-8 lg:col-start-1 lg:row-start-2 lg:flex lg:self-end"
          onPointerEnter={(event) =>
            event.pointerType === "mouse" && setHovered(true)
          }
          onPointerLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={(event) =>
            !event.currentTarget.contains(event.relatedTarget) &&
            setFocused(false)
          }
        >
          {SYSTEMS.map((system, index) => {
            const isActive = index === active;
            const isOpen = isActive || isTabletGrid;
            const bodyId = `how-it-works-${index}`;
            return (
              <li
                key={system.title}
                className="relative grid grid-cols-[auto_1fr] items-center gap-x-6 border-b border-primary pb-4 md:pb-8"
              >
                <system.Icon
                  className={cn(
                    "size-6 shrink-0 transition-colors duration-300 md:size-10",
                    isActive ? "text-brand" : "text-secondary",
                  )}
                />
                {/* An invisible copy with the longest body sets the open
                    item's height; the real title and body center inside it, so
                    every open item is the same height and none sits lopsided. */}
                <div className="grid">
                  <div
                    aria-hidden
                    className="invisible col-start-1 row-start-1"
                  >
                    <p className="text-lg leading-title md:text-xl md:leading-title">
                      {system.title}
                    </p>
                    <Collapse open={isOpen}>
                      <div className="grid">
                        {SYSTEMS.map((other) => (
                          <p
                            key={other.title}
                            className="col-start-1 row-start-1 pt-4 text-sm leading-normal tracking-body"
                          >
                            {other.body}
                          </p>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                  <div className="col-start-1 row-start-1 self-center">
                    {/* The ::after stretches the button over the whole item,
                        icon included, so the item is one click target. */}
                    <h3>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={bodyId}
                        aria-disabled={isActive}
                        onClick={() => {
                          setAutoPlay(false);
                          select(index);
                        }}
                        className="text-balance text-left text-lg leading-title text-primary md:text-xl md:leading-title outline-none after:absolute after:-inset-x-3 after:-top-3 after:bottom-3 after:rounded-2xl focus-visible:after:ring-2 focus-visible:after:ring-brand"
                      >
                        {system.title}
                      </button>
                    </h3>
                    <Collapse id={bodyId} open={isOpen}>
                      <p className="pt-4 text-sm leading-normal tracking-body text-secondary">
                        {system.body}
                      </p>
                    </Collapse>
                  </div>
                </div>
                {isActive && autoPlay && !reduceMotion && (
                  <span
                    aria-hidden
                    className="absolute inset-x-0 -bottom-px h-px origin-left animate-progress bg-accent"
                    style={{
                      animationPlayState:
                        hovered || focused || !isVisible ? "paused" : "running",
                    }}
                    onAnimationEnd={() =>
                      select((index + 1) % SYSTEMS.length, true)
                    }
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const SLIDE_SECONDS = 0.6;

type System = (typeof SYSTEMS)[number];

function Slide({
  from,
  to,
  direction,
  duration,
  onDone,
}: {
  from: System;
  to: System;
  direction: number;
  duration: number;
  onDone: () => void;
}) {
  const transition = { duration, ease: EASE_OUT };
  return (
    <>
      <PhotoSlot
        system={from}
        initial={{ y: "0%" }}
        animate={{ y: `${-direction * 100}%` }}
        transition={transition}
      />
      <PhotoSlot
        system={to}
        initial={{ y: `${direction * 100}%` }}
        animate={{ y: "0%" }}
        transition={transition}
        onAnimationComplete={onDone}
      />
    </>
  );
}

// A slot is one photo plus the 20px gap to its neighbour, so moving a slot by
// its own height lands the next photo exactly in the frame.
function PhotoSlot({
  system,
  ...motionProps
}: { system: System } & ComponentProps<typeof motion.div>) {
  return (
    <motion.div
      className="absolute inset-x-0 top-0 h-[calc(100%+1.25rem)] pb-5"
      {...motionProps}
    >
      <img
        decoding="async"
        src={system.image}
        alt={system.imageAlt}
        className="size-full rounded-card bg-tertiary object-cover"
      />
    </motion.div>
  );
}

function Collapse({
  id,
  open,
  children,
}: {
  id?: string;
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      aria-hidden={!open}
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}
